import React, { useEffect, useMemo, useRef } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import Modal from '@/components/atoms/Modal';
import { Button } from '@/components/atoms/Button';
import { useTheme } from '@emotion/react';
import { useSelector } from 'react-redux';
import { useForm, UseFormReturn } from 'react-hook-form';
import { FetchBaseQueryError } from '@reduxjs/toolkit/dist/query';
import { useCreateCustomerMutation } from '@/services/innovorder/customer/customer.endpoints';
import { getUseCustomerFormAutoGenerateEmailAndPassword } from '@/redux/launchDarkly';
import { getAssertedUserBrandId, getUserToken } from '@/redux/user';
import {
    getFormErrorInfo,
    redirectToCustomerDetailsPage,
    UNKNOWN_ERROR_CODE,
} from '@/utils/factories/customer/customer.utils';
import CreateCustomerForm from './CreateCustomerForm/CreateCustomerForm';
import { StyledModalContainer, StyledClientCreationForm, StyledFormError } from './CreateCustomerModal.styles';
import {
    prepareCreateCustomerPayload,
    generateCreateCustomerFormDefaultValuesWithEmailAndPassword,
    generateCreateCustomerFormDefaultValues,
} from './CreateCustomerModal.utils';
import { BaseCustomerAttributes } from './CreateCustomerModal.types';

interface CreateCustomerModalProps {
    title: string;
    isOpen: boolean;
    onHide: () => void;
}

const CreateCustomerModal: React.FunctionComponent<React.PropsWithChildren<CreateCustomerModalProps>> = ({
    title,
    isOpen,
    onHide,
}) => {
    const theme = useTheme();
    const brandId = useSelector(getAssertedUserBrandId);
    const token = useSelector(getUserToken);
    const customerFormAutoGenerateEmailAndPassword = useSelector(getUseCustomerFormAutoGenerateEmailAndPassword);
    const [createCustomerMutation, { isSuccess, isLoading, data, error }] = useCreateCustomerMutation();
    const intl = useIntl();

    if (isSuccess && data?.customerId) {
        redirectToCustomerDetailsPage(data.customerId);
    }

    const customerForm: UseFormReturn<BaseCustomerAttributes> = useForm<BaseCustomerAttributes>({
        defaultValues: customerFormAutoGenerateEmailAndPassword
            ? generateCreateCustomerFormDefaultValuesWithEmailAndPassword()
            : generateCreateCustomerFormDefaultValues(),
        mode: 'onBlur',
    });

    const createCustomer = () => {
        createCustomerMutation({
            ...prepareCreateCustomerPayload(customerForm.getValues(), brandId),
            token,
        });
    };

    const formError = useMemo(
        () => (error ? getFormErrorInfo(error as FetchBaseQueryError, intl) : null),
        // `useIntl()` returns a new object each render, so we intentionally
        // depend on `error` only. The `intl` reference used when the error
        // arrives is captured for that computation; locale switches mid-error
        // are not expected while the modal is open.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [error],
    );

    const { setError: setCustomerFormError } = customerForm;
    useEffect(() => {
        if (formError && formError.name !== UNKNOWN_ERROR_CODE) {
            setCustomerFormError(
                formError.name as keyof BaseCustomerAttributes,
                { message: formError.message },
                { shouldFocus: true },
            );
        }
    }, [formError, setCustomerFormError]);

    // Scroll all the way to the bottom of the modal's scroll container
    // when the error appears so it is fully visible regardless of which
    // step of the form the user was on (the error box is at the end).
    const errorRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!formError?.message) return;
        const findScrollableParent = (element: HTMLElement | null): HTMLElement | null => {
            let current = element?.parentElement ?? null;
            while (current) {
                const { overflowY } = window.getComputedStyle(current);
                if ((overflowY === 'auto' || overflowY === 'scroll') && current.scrollHeight > current.clientHeight) {
                    return current;
                }
                current = current.parentElement;
            }
            return null;
        };
        const scrollable = findScrollableParent(errorRef.current);
        if (scrollable) {
            scrollable.scrollTo({ top: scrollable.scrollHeight, behavior: 'smooth' });
        } else {
            errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
        }
    }, [formError]);
    const nextButton = (
        <Button
            onClick={createCustomer}
            buttonType="primary"
            disabled={!customerForm.formState.isValid}
            isLoading={isLoading}
        >
            <FormattedMessage id="button.next" />
        </Button>
    );

    return (
        <Modal
            isOpen={isOpen}
            onHide={onHide}
            title={title}
            iconColor={theme.color.bloodyMary}
            buttons={nextButton}
            shouldNotHideOnClickOutside
        >
            <StyledModalContainer>
                <StyledClientCreationForm>
                    <FormattedMessage id="customer.form.modalDescription" />
                    <CreateCustomerForm brandId={brandId} customerForm={customerForm} />
                    {!!formError?.message && (
                        <StyledFormError ref={errorRef} role="alert" data-testid="create-customer-modal-error">
                            {formError.message}
                        </StyledFormError>
                    )}
                </StyledClientCreationForm>
            </StyledModalContainer>
        </Modal>
    );
};

export default CreateCustomerModal;
